import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { Tabs, TabsList, TabsTrigger } from '@components/ui/tabs';
import { Info } from 'lucide-react';
import React, { useRef, useState } from 'react';

import { determineAttemptType } from '@/utils/callsTabs/statusDisplay';

import { getTabLabel, getTabPopoverContent, isTabDisabled } from './tabTypes';
import type { CallsTabsProps, TabItem } from './types';

interface UnifiedTabProps {
  tabItem: TabItem;
  openPopover: string | null;
  setOpenPopover: (value: string | null) => void;
  popoverTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;
  attemptNumber: number;
  totalOfType: number;
  workflowType?: string;
  isHumanAudit: boolean;
  onCancelCall?: (callId: string) => void;
  onRetryTask?: (taskId: string) => void;
  onUpdateChartNotes?: (callId: string, notes: string) => Promise<void>;
  onSelectCall?: (callId: string) => void;
}

const UnifiedTab: React.FC<UnifiedTabProps> = ({
  tabItem,
  openPopover,
  setOpenPopover,
  popoverTimeoutRef,
  attemptNumber,
  totalOfType,
  workflowType,
  isHumanAudit,
  onCancelCall,
  onRetryTask,
  onUpdateChartNotes,
  onSelectCall,
}) => {
  const { type, id, data } = tabItem;
  const PopoverContentComponent = getTabPopoverContent(type);

  const handleMouseEnter = () => {
    if (popoverTimeoutRef.current) {
      clearTimeout(popoverTimeoutRef.current);
      popoverTimeoutRef.current = null;
    }
    setOpenPopover(id);
  };

  const handleMouseLeave = () => {
    popoverTimeoutRef.current = setTimeout(() => {
      setOpenPopover(null);
    }, 300);
  };

  return (
    <Popover
      key={id}
      open={openPopover === id}
      onOpenChange={open => setOpenPopover(open ? id : null)}
    >
      <PopoverTrigger asChild>
        <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <TabsTrigger value={id} className="h-8 text-xs py-1 px-2" disabled={isTabDisabled(type)}>
            <div className="flex flex-col items-start">
              <div className="flex items-center">
                {getTabLabel(tabItem, { attemptNumber, totalOfType, workflowType })}
                <Info className="w-3 h-3 ml-1" />
              </div>
            </div>
          </TabsTrigger>
        </div>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        className="w-auto p-0 border bg-popover shadow-lg"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setOpenPopover(null)}
      >
        <div className="p-4 space-y-3 max-w-[500px]">
          <PopoverContentComponent
            data={data}
            isHumanAudit={isHumanAudit}
            onCancelCall={onCancelCall}
            onRetryTask={onRetryTask}
            onUpdateChartNotes={onUpdateChartNotes}
            onSelectCall={onSelectCall}
            workflowType={workflowType!}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const CallsTabs: React.FC<CallsTabsProps> = ({
  tabItems,
  selectedTabId,
  onTabSelect,
  onCancelCall,
  onRetryTask,
  workflowType,
  isHumanAudit,
  onUpdateChartNotes,
  onSelectCall,
  belowContent,
  trailingContent,
}) => {
  const [openPopover, setOpenPopover] = useState<string | null>(null);
  const popoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Calculate attempt numbers - countable items get incrementing numbers,
  // non-countable items (task, text, email) get 0
  // For MEDICARE_HUMAN workflows, count Primary and Secondary attempts separately
  const getAttemptNumber = (index: number, item: TabItem): number => {
    if (isTabDisabled(item.type)) {
      return 0;
    }

    // Audits don't carry case_state, so they can't share the attempt-type bucket
    // used for calls. Number each audit kind within its own type chronologically
    // (AI Audit 1,2…; Human Audit 1,2…) rather than by overall tab position.
    if (item.type === 'ai_audit' || item.type === 'human_audit') {
      return tabItems.slice(0, index).filter(i => i.type === item.type).length + 1;
    }

    // Get attempt type for current item
    const currentCaseState = 'case_state' in item.data ? item.data.case_state : undefined;
    const currentAttemptType = determineAttemptType(currentCaseState);

    // Count only enabled items of the same attempt type before this one. Audits
    // carry no case_state, so determineAttemptType(undefined) coincidentally matches
    // calls' default attempt type — exclude anything without a case_state so audit
    // tabs don't inflate the call/attempt numbering (BI-1246).
    return (
      tabItems.slice(0, index).filter(i => {
        if (isTabDisabled(i.type)) return false;
        if (!('case_state' in i.data)) return false;
        const caseState = i.data.case_state;
        return determineAttemptType(caseState) === currentAttemptType;
      }).length + 1
    );
  };

  // Total count by tab type — lets handlers (currently only ai_audit)
  // decide whether to suffix the position number on the label.
  const totalByType = tabItems.reduce<Partial<Record<TabItem['type'], number>>>((acc, item) => {
    acc[item.type] = (acc[item.type] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="px-4 pt-2 pb-1 border-b overflow-x-auto">
      <div className="flex items-center gap-2">
        <Tabs value={selectedTabId || ''} onValueChange={onTabSelect}>
          <TabsList className="h-10">
            {tabItems.map((tabItem, index) => (
              <UnifiedTab
                key={tabItem.id}
                tabItem={tabItem}
                openPopover={openPopover}
                setOpenPopover={setOpenPopover}
                popoverTimeoutRef={popoverTimeoutRef}
                attemptNumber={getAttemptNumber(index, tabItem)}
                totalOfType={totalByType[tabItem.type] ?? 1}
                workflowType={workflowType}
                isHumanAudit={isHumanAudit}
                onCancelCall={onCancelCall}
                onRetryTask={onRetryTask}
                onUpdateChartNotes={onUpdateChartNotes}
                onSelectCall={onSelectCall}
              />
            ))}
          </TabsList>
        </Tabs>
        {trailingContent && <div className="ml-auto">{trailingContent}</div>}
      </div>
      {belowContent}
    </div>
  );
};
